import React, { useEffect, useState } from "react";
import {
    Container,
    Card,
    Button,
    Alert,
    Row,
    Col,
    Spinner,
} from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import NavBar from "../NavBar/NavBar";
import Footer from "../Footer/Footer";
import styled from "styled-components";
import axios from "axios";
import { v4 as uuidv4 } from "uuid";

// ---------- styled components ----------
const StatusCard = styled(Card)`
  border-radius: 16px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  border: none;
  overflow: hidden;
  font-size: 16px;
`;

const StatusHeader = styled(Card.Header)`
  background: linear-gradient(135deg, #4a6cf7 0%, #2d4fc7 100%);
  border: none;
  padding: 1.5rem;
  text-align: center;
`;

const DetailBox = styled.div`
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 12px;
  padding: 1rem;
  border: 1px solid #e9ecef;
  height: 100%;
  transition: all 0.2s ease;
  font-size: 14px;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
`;

const ActionButton = styled(Button)`
  border-radius: 50px;
  padding: 0.6rem 1.5rem;
  font-weight: 600;
  transition: all 0.2s ease;
  font-size: 14px;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
`;

const StatusAlert = styled(Alert)`
  border: none;
  border-radius: 12px;
  padding: 1.25rem;
  font-size: 16px;
  background: ${(props) =>
        props.variant === "warning"
            ? "linear-gradient(135deg, #fff3cd 0%, #ffeaa7 100%)"
            : "linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%)"};
  color: ${(props) => (props.variant === "warning" ? "#856404" : "#155724")};
`;

const clientID = "BOAZZ6254G01AZZ";

// ---- API helpers ----
const checkEDDAStatus = async (transactionRef, bankCode) => {
    const requestID = uuidv4();
    const response = await axios.get(
        "https://www.azzenterprises.com/api/v1/edda/status",
        {
            params: {
                boTransactionRefNo: transactionRef,
                applicantBankCode: bankCode,
                clientID,
                requestID,
            },
            withCredentials: true,
        }
    );
    return response.data;
};

const fetchDateRange = async (transactionRef) => {
    try {
        const response = await axios.get(
            `https://www.azzenterprises.com/api/status/update/${transactionRef}`,
            { withCredentials: true }
        );
        return response.data;
    } catch (error) {
        console.error("❌ Error fetching date range:", error);
        return null;
    }
};

const updateCustomerRequestType = async (email, requestType) => {
    try {
        const response = await axios.put(
            `https://www.azzenterprises.com/api/customers/${email}`,
            { requestType },
            { withCredentials: true }
        );
        return response.data;
    } catch (err) {
        console.error("❌ Failed to update customer requestType:", err);
        return null;
    }
};


const GiroStatusPage = () => {
    const location = useLocation();
    const navigate = useNavigate();

    const [data, setData] = useState(null);
    const [isProcessing, setIsProcessing] = useState(true);
    const [errorMsg, setErrorMsg] = useState("");
    const [infoMsg] = useState("");

    useEffect(() => {
        const fetchStatus = async () => {
            try {
                const search = new URLSearchParams(location.search);
                const storedUser = JSON.parse(localStorage.getItem("userData") || "{}");

                let params = {
                    transactionRef: search.get("boTransactionRefNo") || storedUser.boTransactionRefNo,
                    bankCode: search.get("applicantBankCode") || storedUser.applicantBankCode,
                    action:
                        search.get("requestType") === "Cancellation"
                            ? "cancellation"
                            : search.get("requestType") === "Creation"
                                ? "setup"
                                : storedUser.requestType === "Cancellation"
                                    ? "cancellation"
                                    : storedUser.requestType === "Creation"
                                        ? "setup"
                                        : null,
                    ddaReference: search.get("boDDARefNo") || storedUser.boDDARefNo,
                    status: search.get("status") || storedUser.status || "Pending",
                    applicantName: storedUser.full_name,
                    billNumber: storedUser.bill_number,
                    bankName: storedUser.boName,
                };

                console.log(params)

                // Case: no requestType found even in DB → show "not started"
                if (!params.action && storedUser.email_address) {
                    try {
                        const res = await axios.get(
                            `https://www.azzenterprises.com/api/customers/${storedUser.email_address}`
                        );

                        if (!res.data.requestType) {
                            setErrorMsg("eGIRO Creation is not started yet.");
                            setIsProcessing(false);
                            return;
                        }

                        params = { ...params, action: res.data.requestType === "Creation" ? "setup" : "cancellation" };
                    } catch (err) {
                        console.error("❌ Customer lookup failed:", err);
                        setErrorMsg("eGIRO Creation is not started yet.");
                        setIsProcessing(false);
                        return;
                    }
                }

                // If no transactionRef, we can’t proceed
                if (!params.transactionRef || !params.bankCode) {
                    setErrorMsg("Missing transaction reference or bank code from bank");
                    setIsProcessing(false);
                    return;
                }

                // Call EDDA status
                const statusRes = await checkEDDAStatus(params.transactionRef, params.bankCode);
                // const statusRes = {};

                let updated = {
                    ...params,
                    status: statusRes.status || params.status || "Pending",
                };

                if (statusRes.status === "eDDA Cancel Rejected") {
                    // Force requestType → Creation
                    await updateCustomerRequestType(storedUser.email_address, "Creation");

                    // Update local state/params
                    updated = {
                        ...updated,
                        action: "cancellation",
                        status: statusRes.status,
                    };

                    // Also update localStorage userData
                    const newUserData = {
                        ...storedUser,
                        requestType: "Creation",
                    };
                    localStorage.setItem("userData", JSON.stringify(newUserData));
                }

                // Fetch date range if setup
                if (updated.action === "setup") {
                    const dateRange = await fetchDateRange(updated.transactionRef);
                    updated = {
                        ...updated,
                        fromDate: dateRange?.fromDate,
                        toDate: dateRange?.toDate,
                    };

                    const newUserData = {
                        ...storedUser,
                        fromDate: dateRange?.fromDate,
                        toDate: dateRange?.toDate,
                    };
                    localStorage.setItem("userData", JSON.stringify(newUserData));
                }

                setData(updated);
            } catch (err) {
                console.error("❌ Error fetching status:", err);
                setErrorMsg(err.message || "Failed to fetch GIRO status.");
            } finally {
                setIsProcessing(false);
            }
        };

        fetchStatus();
    }, [location.search]);

    const isSetup = data?.action === "setup";
    const isPending = data?.status?.includes("Pending");

    // ---- UI ----
    if (infoMsg) {
        return (
            <>
                <NavBar />
                <Container className="py-5 text-center">
                    <Alert variant="info" className="border-0 rounded-3">
                        <i className="fas fa-info-circle me-2"></i>
                        {infoMsg}
                    </Alert>
                    <ActionButton variant="primary" onClick={() => navigate("/personal")}>
                        Back to Profile
                    </ActionButton>
                </Container>
                <Footer />
            </>
        );
    }

    if (errorMsg) {
        return (
            <>
                <NavBar />
                <Container className="py-5 text-center">
                    <Alert variant="danger" className="border-0 rounded-3">
                        <i className="fas fa-exclamation-triangle me-2"></i>
                        {errorMsg}
                    </Alert>
                    <ActionButton variant="primary" onClick={() => navigate("/personal")}>
                        Back to Profile
                    </ActionButton>
                </Container>
                <Footer />
            </>
        );
    }

    if (!data && !isProcessing) {
        return (
            <>
                <NavBar />
                <Container className="py-5 text-center">
                    <Alert variant="danger" className="border-0 rounded-3">
                        <i className="fas fa-exclamation-triangle me-2"></i>
                        No GIRO status found. Please try again.
                    </Alert>
                    <ActionButton variant="primary" onClick={() => navigate("/personal")}>
                        Back to Profile
                    </ActionButton>
                </Container>
                <Footer />
            </>
        );
    }

    return (
        <>
            <NavBar />
            <Container className="py-5">
                <StatusCard>
                    <StatusHeader className="text-white">
                        <div className="mb-3">
                            <i
                                className={`fas ${isSetup ? "fa-check-circle" : "fa-ban"} fa-2x`}
                                style={{ opacity: 0.9 }}
                            ></i>
                        </div>
                        <h3 style={{ fontSize: "20px", fontWeight: "bold" }}>
                            {isSetup ? "GIRO Setup Status" : "GIRO Cancellation Status"}
                        </h3>
                        <p style={{ fontSize: "14px", marginBottom: 0, opacity: 0.9 }}>
                            {isSetup
                                ? "Your payment arrangement details"
                                : "Your cancellation request details"}
                        </p>
                    </StatusHeader>

                    <Card.Body className="p-4">
                        {isProcessing ? (
                            <div className="text-center py-5">
                                <Spinner animation="border" variant="primary" size="lg" />
                                <p className="text-muted mt-3">Processing your request...</p>
                            </div>
                        ) : (
                            <>
                                <Row className="g-4 mb-4">
                                    <Col md={6}>
                                        <DetailBox>
                                            <small className="text-muted d-block mb-1">
                                                Applicant Name
                                            </small>
                                            <div className="fw-semibold">
                                                {data?.applicantName || "N/A"}
                                            </div>
                                        </DetailBox>
                                    </Col>
                                    <Col md={6}>
                                        <DetailBox>
                                            <small className="text-muted d-block mb-1">
                                                Bill Number
                                            </small>
                                            <div className="fw-semibold">
                                                {data?.billNumber || "N/A"}
                                            </div>
                                        </DetailBox>
                                    </Col>
                                    <Col md={6}>
                                        <DetailBox>
                                            <small className="text-muted d-block mb-1">
                                                DDA Reference
                                            </small>
                                            <div className="fw-semibold">
                                                {data?.ddaReference || "N/A"}
                                            </div>
                                        </DetailBox>
                                    </Col>
                                    <Col md={6}>
                                        <DetailBox>
                                            <small className="text-muted d-block mb-1">
                                                Transaction Ref
                                            </small>
                                            <div className="fw-semibold">
                                                {data?.transactionRef || "N/A"}
                                            </div>
                                        </DetailBox>
                                    </Col>
                                    {data?.bankName && isSetup && (
                                        <Col md={6}>
                                            <DetailBox>
                                                <small className="text-muted d-block mb-1">
                                                    Bank Name
                                                </small>
                                                <div className="fw-semibold">{data.bankName}</div>
                                            </DetailBox>
                                        </Col>
                                    )}
                                    {data?.fromDate && (
                                        <Col md={6}>
                                            <DetailBox>
                                                <small className="text-muted d-block mb-1">
                                                    From Date
                                                </small>
                                                <div className="fw-semibold">{data.fromDate}</div>
                                            </DetailBox>
                                        </Col>
                                    )}
                                    {data?.toDate && (
                                        <Col md={6}>
                                            <DetailBox>
                                                <small className="text-muted d-block mb-1">
                                                    To Date
                                                </small>
                                                <div className="fw-semibold">{data.toDate}</div>
                                            </DetailBox>
                                        </Col>
                                    )}
                                </Row>

                                <StatusAlert
                                    variant={isPending ? "warning" : "success"}
                                    className="mb-4"
                                >
                                    <div className="d-flex align-items-center">
                                        <i
                                            className={`fas ${isPending ? "fa-clock" : "fa-check-circle"
                                                } me-3`}
                                        ></i>
                                        <div>
                                            <h6 className="mb-1">
                                                {isSetup ? "GIRO Setup" : "GIRO Cancellation"} Status
                                            </h6>
                                            <p className="mb-0">{data?.status}</p>
                                        </div>
                                    </div>
                                </StatusAlert>

                                <div className="d-flex flex-wrap gap-3 justify-content-center">
                                    <ActionButton
                                        variant="outline-primary"
                                        onClick={() => navigate("/personal")}
                                    >
                                        <i className="fas fa-user me-2"></i>
                                        Back to Profile
                                    </ActionButton>
                                </div>
                            </>
                        )}
                    </Card.Body>
                </StatusCard>
            </Container>
            <Footer />
        </>
    );
};

export default GiroStatusPage;
